// eslint-disable-next-line workspaces/require-dependency
import { type DefaultRootState } from '@databases/redux/default-root-state';
import {
  type BlogpostReference,
  type BlogpostReferenceOrContentId,
  type OptionalCreatePageReference,
  type OptionalCreatePageReferenceOrContentId
} from '@databases/types/confluence';
import { type DatabaseFieldType, type RawDatabaseValue } from '@databases/types/database';

import {
  blogpostToReference,
  isPageReference,
  pageToReference
} from '../confluence/utils.ts';
import { selectContentByIdOptional } from '../redux/content/selectors.ts';

/**
 * Filters page link values to only include valid entries
 * (those with a contentTitle or contentId).
 */
export function filterValidPageLinkValue(
  value: RawDatabaseValue<DatabaseFieldType.PAGE_LINK>
): Array<
  RawDatabaseValue<DatabaseFieldType.PAGE_LINK>[0] &
    (OptionalCreatePageReferenceOrContentId | BlogpostReferenceOrContentId)
> {
  return value.flatMap((v) => {
    if ('contentTitle' in v || 'contentId' in v) {
      return [v];
    }
    return [];
  });
}

/**
 * Returns true if the given page link field value represents an existing
 * page or blog post, rather than one to be created or an empty value.
 */
export function isExistingPageLinkValue(
  value: RawDatabaseValue<DatabaseFieldType.PAGE_LINK>[0]
): value is RawDatabaseValue<DatabaseFieldType.PAGE_LINK>[0] &
  (OptionalCreatePageReference | BlogpostReference) {
  return (
    ('contentTitle' in value || 'contentId' in value) &&
    (!isPageReference(value) || !value.create)
  );
}

/**
 * Resolves page link values by looking up content in the Redux store.
 * Values with only a contentId are enriched with full page/blogpost references.
 */
export function normalizePageLinkValue(
  value: RawDatabaseValue<DatabaseFieldType.PAGE_LINK>,
  state: DefaultRootState | undefined
): Array<
  RawDatabaseValue<DatabaseFieldType.PAGE_LINK>[0] &
    (OptionalCreatePageReference | BlogpostReference)
> {
  return value.flatMap((v) => {
    const normalized = resolveSinglePageLinkValue(v, state);
    return 'contentTitle' in normalized ? [normalized] : [];
  });
}

/**
 * If the given page value is a page ID reference, attempts to resolve it and return a page reference.
 * If the value is not a page ID reference or the page with the given ID is not in the store,
 * the value is returned unchanged.
 */
export function resolveSinglePageLinkValue(
  value: RawDatabaseValue<DatabaseFieldType.PAGE_LINK>[0],
  state: DefaultRootState | undefined
): RawDatabaseValue<DatabaseFieldType.PAGE_LINK>[0] {
  if ('contentId' in value) {
    const content =
      state && selectContentByIdOptional(state, { contentId: value.contentId });
    if (content?.type === 'page') {
      return { ...pageToReference(content), _id: value._id };
    } else if (content?.type === 'blogpost') {
      return { ...blogpostToReference(content), _id: value._id };
    }
  }
  return value;
}
